import * as React from 'react';
import { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@ooh/ui/components/sonner';
import type { MapViewHandle, MapPin, DrawingTool } from '@ooh/ui/components/map-view';
import type { AreaSelectionActions } from '../../features/map-area-selection';
import { getPinsInsideShape } from '../../features/map-area-selection/lib';
import { dispatchAreaSelection } from '../../features/map-area-selection/lib/drawing-tool';

interface UseAreaSelectionHandlersOptions {
  onShapeDrawn?: () => void;
  onShapesCleared?: () => void;
}

// eslint-disable-next-line max-lines-per-function -- draw+progress+clear+draft handlers are cohesive; extracting further would reduce readability
export function useAreaSelectionHandlers(
  mapPins: MapPin[],
  actions: AreaSelectionActions,
  options?: UseAreaSelectionHandlersOptions,
) {
  const { onShapeDrawn, onShapesCleared } = options ?? {};
  const { t } = useTranslation('map');
  const mapViewRef = useRef<MapViewHandle>(null);
  const [drawProgressCount, setDrawProgressCount] = React.useState<number | null>(null);
  // IDs handed to the quick-add picker when the user opens the add-to-offer flow.
  const [pickerMediaUnitIds, setPickerMediaUnitIds] = React.useState<Array<string | number>>([]);
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);

  const handleDrawComplete = useCallback(
    (geometry: GeoJSON.Polygon, toolUsed: DrawingTool) => {
      setDrawProgressCount(null);
      const matchedIds = getPinsInsideShape(mapPins, geometry);
      if (matchedIds.length === 0) {
        toast.info(t('areaSelection.emptyShapeToast'));
        return;
      }
      dispatchAreaSelection(matchedIds, toolUsed, actions);
      onShapeDrawn?.();
    },
    [mapPins, actions, t, onShapeDrawn],
  );

  const handleDrawProgress = useCallback(
    (geometry: GeoJSON.Polygon | null, _toolUsed: DrawingTool) => {
      if (!geometry) {
        setDrawProgressCount(null);
        return;
      }
      setDrawProgressCount(getPinsInsideShape(mapPins, geometry).length);
    },
    [mapPins],
  );

  const handleClearShapes = useCallback(() => {
    mapViewRef.current?.clearAllShapes();
    actions.clear();
    onShapesCleared?.();
  }, [actions, onShapesCleared]);

  // Opens the shared QuickAddOfferPicker with the area selection's unit ids.
  // The picker (rendered by the map page) reuses the CU-86c8zc5uw quick-add flow.
  const handleAddToDraft = useCallback((ids: (string | number)[]) => {
    setPickerMediaUnitIds(ids);
    setIsPickerOpen(true);
  }, []);

  const closePicker = useCallback(() => {
    setIsPickerOpen(false);
  }, []);

  // On a successful add, clear both the selection set and the drawn shapes.
  const handleAddToDraftSuccess = useCallback(() => {
    setIsPickerOpen(false);
    actions.clear();
    mapViewRef.current?.clearAllShapes();
    onShapesCleared?.();
  }, [actions, onShapesCleared]);

  return {
    mapViewRef,
    drawProgressCount,
    handleDrawComplete,
    handleDrawProgress,
    handleClearShapes,
    handleAddToDraft,
    isPickerOpen,
    pickerMediaUnitIds,
    closePicker,
    handleAddToDraftSuccess,
  };
}
